import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Webhook, Check, X, Loader2, RefreshCw, ExternalLink,
  AlertCircle, CheckCircle2, Clock, Zap, Plus, Trash2, Edit2, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebhookManager() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/webhooks/config`);
      setConfig(res.data);
      setLogs(res.data.recent_events || []);
    } catch (err) {
      console.error('Failed to load webhook config:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/webhooks/config`, {
        webhook_secret: config.webhook_secret,
        endpoint_url: config.endpoint_url,
        enabled: config.enabled,
      });
      toast.success('Webhook configuration saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/webhooks/test`);
      toast.success(res.data.message || 'Test event sent');
      loadConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stripe Webhook Config */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Webhook className="w-4 h-4 text-[#6D4AFF]" />
              Stripe Webhook
            </CardTitle>
            <Badge className={config?.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
              {config?.enabled ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Endpoint URL</Label>
            <Input
              value={config?.endpoint_url || ''}
              onChange={(e) => setConfig({ ...config, endpoint_url: e.target.value })}
              className="bg-[#252540] border-[#2d2d44] text-white font-mono text-sm"
              placeholder="https://mypatriotmail.org/api/webhook/stripe"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Webhook Secret</Label>
            <Input
              value={config?.webhook_secret || ''}
              onChange={(e) => setConfig({ ...config, webhook_secret: e.target.value })}
              className="bg-[#252540] border-[#2d2d44] text-white font-mono text-sm"
              type="password"
              placeholder="whsec_..."
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
            <div>
              <p className="text-sm text-white font-medium">Enable Webhook</p>
              <p className="text-xs text-gray-500">Process incoming Stripe events</p>
            </div>
            <Switch
              checked={config?.enabled || false}
              onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={saveConfig}
              disabled={saving}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <Button
              onClick={testWebhook}
              disabled={testing}
              variant="outline"
              className="border-[#2d2d44] text-gray-300"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Test
            </Button>
            <Button
              onClick={loadConfig}
              variant="outline"
              className="border-[#2d2d44] text-gray-400"
              size="icon"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Listening Events */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            Listening Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(config?.events || []).map((evt, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${evt.active ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                  <code className="text-sm text-white font-mono">{evt.name}</code>
                </div>
                <span className="text-xs text-gray-500">{evt.description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Events Log */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-400" />
              Recent Events
              {logs.length > 0 && (
                <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] text-xs">{logs.length}</Badge>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-6">
              <Clock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No webhook events received yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {logs.map((log, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
                  <div className="flex items-center gap-3">
                    {log.status === 'success' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    )}
                    <div>
                      <code className="text-xs text-white font-mono">{log.event_type}</code>
                      {log.user_email && (
                        <p className="text-xs text-gray-500">{log.user_email}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-500">{log.timestamp}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
